import { LayoutComponent } from './layouts/layout/layout.component';
import { VexRoutes } from '@vex/interfaces/vex-route.interface';
import { authGuard } from './core/auth/auth.guard';

export const appRoutes: VexRoutes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/auth/login-invertus/login-invertus.component').then(
        (m) => m.LoginInvertusComponent
      )
  },
  {
    path: 'register',
    loadComponent: () =>
      import('./pages/pages/auth/register/register.component').then(
        (m) => m.RegisterComponent
      )
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import(
        './pages/pages/auth/forgot-password/forgot-password.component'
      ).then((m) => m.ForgotPasswordComponent)
  },
  {
    path: 'coming-soon',
    loadComponent: () =>
      import('./pages/pages/coming-soon/coming-soon.component').then(
        (m) => m.ComingSoonComponent
      )
  },
  // Cambio de contraseña (obligatorio tras login con temporal, o voluntario).
  // Va FUERA del layout: pantalla completa sin sidebar. `authGuard` exige
  // sesión pero permite esta ruta aun con `debeCambiarPassword`.
  {
    path: 'cambiar-password',
    canActivate: [authGuard],
    loadComponent: () =>
      import(
        './pages/apps/admin/configuracion/usuarios-admin/cambio-password/cambio-password.component'
      ).then((m) => m.CambioPasswordComponent)
  },
  // Ruta PÚBLICA — asistente de convocatoria (sin layout de admin, sin guard).
  // Es la puerta única: informa sobre vacantes, recibe hojas de vida de gente
  // nueva y, si dan su cédula, consulta el estado de un proceso existente.
  // Pensada para que la enlacen desde un botón en otra web del cliente.
  {
    path: 'convocatoria',
    loadComponent: () =>
      import('./pages/publico/asistente-publico/asistente-publico.component').then(
        (m) => m.AsistentePublicoComponent
      )
  },
  // El chat viejo de candidatos quedó absorbido por /convocatoria.
  { path: 'candidatos/chat', redirectTo: 'convocatoria', pathMatch: 'full' },
  // Ruta PÚBLICA — formulario de contacto de Infinitus (sin layout, sin guard).
  // El endpoint `POST /publico/inmobiliario/contacto` existía hace tiempo pero
  // no había página que lo consumiera: QA lo reportó el 31/08 (L-05/L-06).
  // Acepta `?inmueble=<id>` para enlazarla desde una ficha de propiedad.
  {
    path: 'contacto',
    loadComponent: () =>
      import('./pages/publico/contacto-inmobiliario/contacto-inmobiliario.component').then(
        (m) => m.ContactoInmobiliarioComponent
      )
  },
  // Asistente dedicado por empresa, en ventana propia (se abre con
  // window.open desde el menú). Va FUERA del LayoutComponent para que no
  // arrastre sidebar/toolbar, pero CON authGuard: sin sesión válida redirige
  // a /login. El JWT vive en localStorage, así que la ventana hereda la
  // sesión del CRM sin pasar nada por la URL.
  {
    path: 'asistente/ventana/:empresa',
    canActivate: [authGuard],
    loadComponent: () =>
      import(
        './pages/apps/asistente/asistente-ventana/asistente-ventana.component'
      ).then((m) => m.AsistenteVentanaComponent)
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard],
    // canActivateChild corre en cada navegación interna (no sólo al montar el
    // layout): así el cambio de contraseña obligatorio bloquea todas las rutas.
    canActivateChild: [authGuard],
    children: [
      {
        path: 'dashboards/analytics',
        redirectTo: '/',
        pathMatch: 'full'
      },
      {
        path: '',
        loadComponent: () =>
          import('./pages/dashboards/invertus/dashboard-invertus.component').then(
            (m) => m.DashboardInvertusComponent
          )
      },
      {
        path: 'apps',
        children: [
          // ── Invertus ───────────────────────────────────────────────────
          {
            path: 'admin',
            loadChildren: () => import('./pages/apps/admin/admin.routes')
          },
          {
            path: 'asistente',
            loadChildren: () =>
              import('./pages/apps/asistente/asistente.routes')
          },
          {
            path: 'invering',
            loadChildren: () => import('./pages/apps/invering/invering.routes')
          },
          {
            path: 'infinitus',
            loadChildren: () => import('./pages/apps/infinitus/infinitus.routes')
          },
          {
            path: 'community',
            loadChildren: () =>
              import('./pages/apps/community/community.routes')
          },
          {
            path: 'proyectos',
            children: [
              {
                path: 'contratos',
                loadChildren: () =>
                  import('./pages/apps/proyectos/contratos/contratos.routes')
              },
              {
                path: 'tareas',
                loadChildren: () =>
                  import('./pages/apps/proyectos/tasks/tasks.routes')
              }
            ]
          },
          {
            path: 'gh',
            children: [
              {
                path: 'requisiciones',
                loadChildren: () =>
                  import('./pages/apps/gh/requisiciones/requisiciones.routes')
              },
              {
                path: 'seleccion',
                loadChildren: () =>
                  import('./pages/apps/gh/seleccion/seleccion.routes')
              },
              {
                path: 'contratacion',
                loadComponent: () =>
                  import('./pages/apps/gh/contratacion/contratacion.component').then(
                    (m) => m.ContratacionComponent
                  )
              },
              {
                path: 'induccion',
                loadComponent: () =>
                  import('./pages/apps/gh/induccion/induccion.component').then(
                    (m) => m.InduccionComponent
                  )
              },
              {
                path: 'desercion',
                loadComponent: () =>
                  import('./pages/apps/gh/desercion/desercion.component').then(
                    (m) => m.DesercionComponent
                  )
              },
              {
                path: 'convocatorias',
                loadComponent: () =>
                  import('./pages/apps/gh/convocatorias/convocatorias.component').then(
                    (m) => m.ConvocatoriasComponent
                  )
              }
            ]
          },
          // ── Vex demo (mantener hasta limpiar después de entrega #1) ───
          {
            path: 'chat',
            loadChildren: () => import('./pages/apps/chat/chat.routes')
          },
          {
            path: 'mail',
            loadChildren: () => import('./pages/apps/mail/mail.routes'),
            data: {
              toolbarShadowEnabled: true,
              scrollDisabled: true
            }
          },
          {
            path: 'social',
            loadChildren: () => import('./pages/apps/social/social.routes')
          },
          {
            path: 'contacts',
            loadChildren: () => import('./pages/apps/contacts/contacts.routes')
          },
          {
            path: 'calendar',
            loadComponent: () =>
              import('./pages/apps/calendar/calendar.component').then(
                (m) => m.CalendarComponent
              ),
            data: {
              toolbarShadowEnabled: true
            }
          },
          {
            path: 'aio-table',
            loadComponent: () =>
              import('./pages/apps/aio-table/aio-table.component').then(
                (m) => m.AioTableComponent
              ),
            data: {
              toolbarShadowEnabled: false
            }
          },
          {
            path: 'help-center',
            loadChildren: () =>
              import('./pages/apps/help-center/help-center.routes')
          },
          {
            path: 'scrumboard',
            loadChildren: () =>
              import('./pages/apps/scrumboard/scrumboard.routes')
          },
          {
            path: 'editor',
            loadComponent: () =>
              import('./pages/apps/editor/editor.component').then(
                (m) => m.EditorComponent
              ),
            data: {
              scrollDisabled: true
            }
          }
        ]
      },
      {
        path: 'pages',
        children: [
          {
            path: 'pricing',
            loadComponent: () =>
              import('./pages/pages/pricing/pricing.component').then(
                (m) => m.PricingComponent
              )
          },
          {
            path: 'faq',
            loadComponent: () =>
              import('./pages/pages/faq/faq.component').then(
                (m) => m.FaqComponent
              )
          },
          {
            path: 'guides',
            loadComponent: () =>
              import('./pages/pages/guides/guides.component').then(
                (m) => m.GuidesComponent
              )
          },
          {
            path: 'invoice',
            loadComponent: () =>
              import('./pages/pages/invoice/invoice.component').then(
                (m) => m.InvoiceComponent
              )
          },
          {
            path: 'error-404',
            loadComponent: () =>
              import('./pages/pages/errors/error-404/error-404.component').then(
                (m) => m.Error404Component
              )
          },
          {
            path: 'error-500',
            loadComponent: () =>
              import('./pages/pages/errors/error-500/error-500.component').then(
                (m) => m.Error500Component
              )
          }
        ]
      },
      {
        path: 'ui',
        children: [
          {
            path: 'components',
            loadChildren: () =>
              import('./pages/ui/components/components.routes')
          },
          {
            path: 'forms/form-elements',
            loadComponent: () =>
              import(
                './pages/ui/forms/form-elements/form-elements.component'
              ).then((m) => m.FormElementsComponent)
          },
          {
            path: 'forms/form-wizard',
            loadComponent: () =>
              import('./pages/ui/forms/form-wizard/form-wizard.component').then(
                (m) => m.FormWizardComponent
              )
          },
          {
            path: 'icons',
            loadChildren: () => import('./pages/ui/icons/icons.routes')
          },
          {
            path: 'page-layouts',
            loadChildren: () =>
              import('./pages/ui/page-layouts/page-layouts.routes')
          }
        ]
      },
      {
        path: 'documentation',
        loadChildren: () => import('./pages/documentation/documentation.routes')
      },
      {
        path: '**',
        loadComponent: () =>
          import('./pages/pages/errors/error-404/error-404.component').then(
            (m) => m.Error404Component
          )
      }
    ]
  }
];
